import { createContext, useContext, useState, useCallback } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, CheckCircle2 } from "lucide-react";
import { createLead, formatApiErrorDetail } from "@/lib/api";
import PhoneOtpField from "@/components/shared/PhoneOtpField";

const LeadDialogContext = createContext(null);

export const useLeadDialog = () => {
  const ctx = useContext(LeadDialogContext);
  if (!ctx) throw new Error("useLeadDialog must be used within LeadDialogProvider");
  return ctx;
};

const FIELD =
  "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-[15px] text-[#111111] placeholder:text-gray-400 focus:outline-none focus:border-[#0B63F6] focus:ring-1 focus:ring-[#0B63F6] transition-colors";

export function LeadDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ source: "contact", service: "", title: "Request AI Visibility Audit" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [countryCode, setCountryCode] = useState("+91");
  const [otpToken, setOtpToken] = useState(null);

  const openLead = useCallback((opts = {}) => {
    setMeta({
      source: opts.source || "contact",
      service: opts.service || "",
      title: opts.title || "Request AI Visibility Audit",
    });
    setDone(false);
    setOtpToken(null);
    setForm({ name: "", email: "", company: "", phone: "", message: "" });
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    if (!otpToken) {
      toast.error("Please verify your WhatsApp number with the OTP.");
      return;
    }
    setSubmitting(true);
    try {
      await createLead({
        ...form,
        country_code: countryCode,
        phone: form.phone,
        service_interest: meta.service,
        source: meta.source,
        otp_token: otpToken,
      });
      setDone(true);
      toast.success("Thank you — our GEO team will reach out shortly.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
      setOtpToken(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <LeadDialogContext.Provider value={{ openLead }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lead-dialog"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
            <motion.div
              className="relative w-full max-w-lg bg-white rounded-2xl border border-gray-200 shadow-2xl p-8"
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={close}
                aria-label="Close"
                data-testid="lead-dialog-close"
                className="absolute right-5 top-5 text-gray-400 hover:text-[#111111] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {done ? (
                <div className="py-8 text-center">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-[#19A974]" />
                  <h3 className="font-display text-2xl font-bold mt-4">Request received</h3>
                  <p className="text-gray-600 mt-2">
                    A CompuBrain AI GEO expert will contact you shortly to plan your next steps.
                  </p>
                  <button
                    onClick={close}
                    data-testid="lead-dialog-done"
                    className="mt-6 bg-[#111111] text-white rounded-full px-7 py-3 font-medium hover:bg-[#0B63F6] transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B63F6]">CompuBrain AI</p>
                  <h3 className="font-display text-2xl font-bold tracking-tightest mt-2">{meta.title}</h3>
                  <p className="text-gray-600 text-sm mt-2">
                    Tell us about your business and we&apos;ll respond with tailored GEO next steps.
                  </p>
                  <form onSubmit={submit} className="mt-6 space-y-3" data-testid="lead-form">
                    <input className={FIELD} placeholder="Full name*" value={form.name} onChange={set("name")} data-testid="lead-name" />
                    <input className={FIELD} type="email" placeholder="Work email*" value={form.email} onChange={set("email")} data-testid="lead-email" />
                    <input className={FIELD} placeholder="Company" value={form.company} onChange={set("company")} data-testid="lead-company" />
                    <PhoneOtpField
                      countryCode={countryCode}
                      phone={form.phone}
                      onCountryCode={setCountryCode}
                      onPhone={(v) => setForm((f) => ({ ...f, phone: v }))}
                      token={otpToken}
                      onToken={setOtpToken}
                    />
                    <textarea className={`${FIELD} min-h-[90px] resize-none`} placeholder="What would you like to achieve?" value={form.message} onChange={set("message")} data-testid="lead-message" />
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="lead-submit"
                      className="w-full bg-[#0B63F6] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#6E56CF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {submitting ? "Sending..." : "Submit request"}
                      {!submitting && <ArrowRight className="h-4 w-4" />}
                    </button>
                    <p className="text-[11px] text-gray-400 text-center">
                      We respect your privacy. Your details are used only to respond to your enquiry.
                    </p>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LeadDialogContext.Provider>
  );
}
